import ContactAPI from '../../../api/contacts';
import filterQueryGenerator from '../../../helper/filterQueryGenerator';
import { UNASSIGNED_KANBAN_COLUMN_VALUE } from 'dashboard/constants/kanban';
import {
  SET_KANBAN_GROUP_BY_ATTRIBUTE_KEY,
  SET_KANBAN_UI_FLAG,
  SET_KANBAN_COLUMN_FETCHING,
  SET_KANBAN_COLUMN_CONTACTS,
  MOVE_KANBAN_CONTACT,
  RESET_KANBAN_COLUMNS,
} from './mutations';

const PER_PAGE = 15;

const buildColumnQueryPayload = ({ baseFilters, attributeKey, columnValue }) =>
  filterQueryGenerator([
    ...baseFilters,
    columnValue === UNASSIGNED_KANBAN_COLUMN_VALUE
      ? {
          attribute_key: attributeKey,
          filter_operator: 'is_not_present',
          values: [],
          query_operator: 'and',
        }
      : {
          attribute_key: attributeKey,
          filter_operator: 'equal_to',
          values: [columnValue],
          query_operator: 'and',
        },
  ]);

const buildColumnMeta = meta => ({
  currentPage: Number(meta.current_page),
  count: meta.count,
  hasMore: Number(meta.current_page) * PER_PAGE < meta.count,
});

export const actions = {
  resetColumns({ commit }) {
    commit(RESET_KANBAN_COLUMNS);
  },

  fetchColumns: async (
    { dispatch, commit },
    { attributeKey, columnValues, baseFilters = [] }
  ) => {
    commit(SET_KANBAN_UI_FLAG, { isFetchingColumns: true });
    commit(SET_KANBAN_GROUP_BY_ATTRIBUTE_KEY, attributeKey);
    commit(RESET_KANBAN_COLUMNS);
    try {
      await Promise.all(
        columnValues.map(columnValue =>
          dispatch('fetchColumn', { attributeKey, columnValue, baseFilters })
        )
      );
    } finally {
      commit(SET_KANBAN_UI_FLAG, { isFetchingColumns: false });
    }
  },

  fetchColumn: async (
    { commit },
    { attributeKey, columnValue, baseFilters = [], page = 1 }
  ) => {
    commit(SET_KANBAN_COLUMN_FETCHING, { columnValue, isFetching: true });
    try {
      const queryPayload = buildColumnQueryPayload({
        baseFilters,
        attributeKey,
        columnValue,
      });
      const {
        data: { payload, meta },
      } = await ContactAPI.filter(page, 'name', queryPayload);
      commit(SET_KANBAN_COLUMN_CONTACTS, {
        columnValue,
        contacts: payload,
        meta: buildColumnMeta(meta),
        append: false,
      });
    } catch (error) {
      commit(SET_KANBAN_COLUMN_FETCHING, { columnValue, isFetching: false });
    }
  },

  loadMoreColumn: async (
    { commit, state },
    { attributeKey, columnValue, baseFilters = [] }
  ) => {
    const column = state.columns[columnValue];
    if (!column || !column.meta.hasMore || column.isFetchingMore) return;

    commit(SET_KANBAN_COLUMN_FETCHING, { columnValue, isFetchingMore: true });
    try {
      const queryPayload = buildColumnQueryPayload({
        baseFilters,
        attributeKey,
        columnValue,
      });
      const {
        data: { payload, meta },
      } = await ContactAPI.filter(
        column.meta.currentPage + 1,
        'name',
        queryPayload
      );
      commit(SET_KANBAN_COLUMN_CONTACTS, {
        columnValue,
        contacts: payload,
        meta: buildColumnMeta(meta),
        append: true,
      });
    } catch (error) {
      commit(SET_KANBAN_COLUMN_FETCHING, {
        columnValue,
        isFetchingMore: false,
      });
    }
  },

  moveContact: async (
    { commit },
    { contactId, fromColumnValue, toColumnValue, attributeKey }
  ) => {
    commit(MOVE_KANBAN_CONTACT, {
      contactId,
      fromColumnValue,
      toColumnValue,
    });
    try {
      await ContactAPI.update(contactId, {
        custom_attributes: {
          [attributeKey]:
            toColumnValue === UNASSIGNED_KANBAN_COLUMN_VALUE
              ? ''
              : toColumnValue,
        },
      });
    } catch (error) {
      commit(MOVE_KANBAN_CONTACT, {
        contactId,
        fromColumnValue: toColumnValue,
        toColumnValue: fromColumnValue,
      });
      throw error;
    }
  },
};
